import api, { type AchievementsV1Response } from '@/services/api'
import { computed, ref } from 'vue'
import { inventoryItems, type PlayerInventoryItem } from '@/composables/game/inventory'
import { availableBadges, badgeIdsWithCount, type OrderedBadge } from '@/composables/game/badge'
import { DateTime } from 'luxon'
import { INVENTORY_ID } from '@/types/shared/player'

const season1Stats = ref<AchievementsV1Response | undefined>(undefined)

export const useDropeeSeason1 = () => {
  const season1PlayerInventory = computed<PlayerInventoryItem[]>(() => {
    if (!season1Stats.value?.playerStats.inventory) return []

    const inventory = inventoryItems
      .map((item) => {
        const count = season1Stats.value?.playerStats.inventory[item.id] ?? 0
        return {
          ...item,
          count,
        }
      })
      .filter((item) => item.count > 0 && !item.season2Only)

    // Check if Element Multiplier inventory item collected which doesn't come from BE:
    const ember = season1Stats.value.playerStats.inventory[INVENTORY_ID.EMBER_ELEMENT_SHARD] ?? 0
    const frost = season1Stats.value.playerStats.inventory[INVENTORY_ID.FROST_ELEMENT_SHARD] ?? 0
    const volt = season1Stats.value.playerStats.inventory[INVENTORY_ID.VOLT_ELEMENT_SHARD] ?? 0
    const stone = season1Stats.value.playerStats.inventory[INVENTORY_ID.STONE_ELEMENT_SHARD] ?? 0
    if (Math.min(ember, frost, volt, stone) > 0) {
      const elementMultiplier = inventoryItems.find((i) => i.id === INVENTORY_ID.ELEMENT_MULTIPLIER)
      if (elementMultiplier && !inventory.some(i => i.id === INVENTORY_ID.ELEMENT_MULTIPLIER)) {
        // Find any of the element shards to insert before them
        const shardIds = [
          INVENTORY_ID.EMBER_ELEMENT_SHARD,
          INVENTORY_ID.FROST_ELEMENT_SHARD,
          INVENTORY_ID.VOLT_ELEMENT_SHARD,
          INVENTORY_ID.STONE_ELEMENT_SHARD,
        ]
        const firstShardIndex = inventory.findIndex(i => shardIds.includes(i.id))

        if (firstShardIndex !== -1) {
          inventory.splice(firstShardIndex, 0, {
            ...elementMultiplier,
            count: 1,
          })
        } else {
          inventory.unshift({
            ...elementMultiplier,
            count: 1,
          })
        }
      }
    }

    return inventory
  })

  const season1PlayerBadges = computed<{
    badge: OrderedBadge,
    count: number,
    showCount?: boolean
  }[]>(() =>
    availableBadges.map((item) => {
      return { badge: item, count: season1Stats.value?.badges[item.id] ?? 0, showCount: badgeIdsWithCount.includes(item.id), season2Only: item.season2Only }
    }).filter((item) => !item.season2Only))

  const season1Rank = computed(() => season1Stats.value?.currentLevelRank)
  const effortLeaderboardSeason1Rank = computed(() => season1Stats.value?.effortLeaderboardCurrentRank)
  const season1PlayerLevel = computed(() => season1Stats.value?.playerStats.level ?? 0)
  const snapshotTimestamp = 1766131200 // 2025-12-19

  const season1PlayerAchievements = computed(() => {
    return {
      cardsUpgrade: season1Stats.value?.cards.upgrades,
      friends: season1Stats.value?.playerStats.friends.count,
      tonTxCount: season1Stats.value?.playerStats.ecommerce.tonTransactionsCount,
      tonDailyCheckin: season1Stats.value?.playerStats.tasks.tonDailyCheckinCredits,
      dappRadarTtm: season1Stats.value?.playerStats.tasks.counters.dappRadarTtm,
      dailyCheckinStreaks: season1Stats.value?.playerStats.tasks.dailyCheckin.maxConsecutiveDays,
      spins: season1Stats.value?.totalSpins,
      pathwayCompletionsCount: season1Stats.value?.playerStats.pathwayEvents.completionsCount,
      pathwayStepCompletionsCount: season1Stats.value?.playerStats.pathwayEvents.stepCompletionsCount,
      revealedMysteryBoxes: season1Stats.value?.playerStats.ecommerce.revealedMysteryBoxes,
      dailyCombo: season1Stats.value?.playerStats.challenges.completionsCount.dailyCombo,
      cardsAbove15: season1Stats.value?.cards.above15,
      cardsAbove25: season1Stats.value?.cards.above25,
      cardsAbove50: season1Stats.value?.cards.above50,
      cardsAbove100: season1Stats.value?.cards.above100,
      cardsAbove150: season1Stats.value?.cards.above150,
      specialCards: season1Stats.value?.cards.special,
      tasks: season1Stats.value?.tasks,
      badges: season1PlayerBadges.value.reduce((sum, item) => sum + item.count, 0),
      daysSinceOnboarding: Math.floor(DateTime.fromSeconds(snapshotTimestamp).diff(DateTime.fromSeconds(season1Stats.value?.daysSinceOnboarding ?? 0), 'days').days),
    }
  })

  async function getSeason1Stats() {
    const response = await api.getAchievementsV1()
    if (typeof response === 'object' && response !== null && 'playerStats' in response) {
      season1Stats.value = response
    }
  }

  return {
    getSeason1Stats,
    season1PlayerInventory,
    season1PlayerLevel,
    season1PlayerAchievements,
    season1PlayerBadges,
    season1Rank,
    effortLeaderboardSeason1Rank,
    season1Stats,
  }
}
